import { __decorate, __extends } from "tslib";
import { coerceBoolean, coerceNumber, CustomElement, FoundationProperty, upgradeProperty } from '@tyler-components-web/core';
import { tylIconArrowDownward } from '@tylertech/tyler-icons/standard';
import { ExpansionPanelComponent } from '../expansion-panel';
import { TableAdapter } from './table-adapter';
import { TABLE_CONSTANTS } from './table-constants';
import { TableFoundation } from './table-foundation';
import { CellAlign } from './types';
import { TylerIconRegistry, IconComponent } from '../icon';
/**
 * The custom element class behind the `tcw-table` component.
 */
var TableComponent = /** @class */ (function (_super) {
    __extends(TableComponent, _super);
    function TableComponent() {
        var _this = _super.call(this) || this;
        TylerIconRegistry.define(tylIconArrowDownward);
        _this._foundation = new TableFoundation(new TableAdapter(_this));
        return _this;
    }
    Object.defineProperty(TableComponent, "observedAttributes", {
        get: function () {
            return [
                TABLE_CONSTANTS.attributes.SELECT,
                TABLE_CONSTANTS.attributes.MULTISELECT,
                TABLE_CONSTANTS.attributes.SELECT_KEY,
                TABLE_CONSTANTS.attributes.DENSE,
                TABLE_CONSTANTS.attributes.ROOMY,
                TABLE_CONSTANTS.attributes.FILTER,
                TABLE_CONSTANTS.attributes.FIXED_HEADERS,
                TABLE_CONSTANTS.attributes.LAYOUT_TYPE,
                TABLE_CONSTANTS.attributes.WRAP_CONTENT,
                TABLE_CONSTANTS.attributes.RESIZABLE,
                TABLE_CONSTANTS.attributes.MIN_RESIZE_WIDTH,
                TABLE_CONSTANTS.attributes.ALLOW_ROW_CLICK,
                TABLE_CONSTANTS.attributes.MULTI_COLUMN_SORT,
                TABLE_CONSTANTS.attributes.SELECT_CHECKBOX_ALIGNMENT,
                TABLE_CONSTANTS.attributes.TOOLTIP_SELECT,
                TABLE_CONSTANTS.attributes.TOOLTIP_SELECT_ALL
            ];
        },
        enumerable: true,
        configurable: true
    });
    TableComponent.prototype.initializedCallback = function () {
        upgradeProperty(this, 'data');
        upgradeProperty(this, 'columnConfigurations');
        upgradeProperty(this, 'select');
        upgradeProperty(this, 'multiselect');
        upgradeProperty(this, 'selectKey');
        upgradeProperty(this, 'dense');
        upgradeProperty(this, 'roomy');
        upgradeProperty(this, 'filter');
        upgradeProperty(this, 'fixedHeaders');
        upgradeProperty(this, 'layoutType');
        upgradeProperty(this, 'wrapContent');
        upgradeProperty(this, 'resizable');
        upgradeProperty(this, 'minResizeWidth');
        upgradeProperty(this, 'allowRowClick');
        upgradeProperty(this, 'multiColumnSort');
        upgradeProperty(this, 'cellCreated');
        upgradeProperty(this, 'rowCreated');
        upgradeProperty(this, 'selectAllTemplate');
        upgradeProperty(this, 'selectCheckboxAlignment');
        upgradeProperty(this, 'tooltipSelect');
        upgradeProperty(this, 'tooltipSelectAll');
        this._foundation.initialize();
    };
    TableComponent.prototype.connectedCallback = function () {
        this._foundation.connect();
    };
    TableComponent.prototype.disconnectedCallback = function () {
        this._foundation.disconnect();
    };
    TableComponent.prototype.attributeChangedCallback = function (name, oldValue, newValue) {
        switch (name) {
            case TABLE_CONSTANTS.attributes.SELECT:
                this.select = coerceBoolean(newValue);
                break;
            case TABLE_CONSTANTS.attributes.MULTISELECT:
                this.multiselect = coerceBoolean(newValue);
                break;
            case TABLE_CONSTANTS.attributes.SELECT_KEY:
                this.selectKey = newValue;
                break;
            case TABLE_CONSTANTS.attributes.DENSE:
                this.dense = coerceBoolean(newValue);
                break;
            case TABLE_CONSTANTS.attributes.ROOMY:
                this.roomy = coerceBoolean(newValue);
                break;
            case TABLE_CONSTANTS.attributes.FILTER:
                this.filter = coerceBoolean(newValue);
                break;
            case TABLE_CONSTANTS.attributes.FIXED_HEADERS:
                this.fixedHeaders = this.hasAttribute(TABLE_CONSTANTS.attributes.FIXED_HEADERS);
                break;
            case TABLE_CONSTANTS.attributes.LAYOUT_TYPE:
                this.layoutType = newValue;
                break;
            case TABLE_CONSTANTS.attributes.WRAP_CONTENT:
                this.wrapContent = coerceBoolean(newValue);
                break;
            case TABLE_CONSTANTS.attributes.RESIZABLE:
                this.resizable = this.hasAttribute(TABLE_CONSTANTS.attributes.RESIZABLE);
                break;
            case TABLE_CONSTANTS.attributes.MIN_RESIZE_WIDTH:
                this.minResizeWidth = coerceNumber(newValue);
                break;
            case TABLE_CONSTANTS.attributes.ALLOW_ROW_CLICK:
                this.allowRowClick = this.hasAttribute(TABLE_CONSTANTS.attributes.ALLOW_ROW_CLICK);
                break;
            case TABLE_CONSTANTS.attributes.MULTI_COLUMN_SORT:
                this.multiColumnSort = this.hasAttribute(TABLE_CONSTANTS.attributes.MULTI_COLUMN_SORT);
                break;
            case TABLE_CONSTANTS.attributes.SELECT_CHECKBOX_ALIGNMENT:
                this.selectCheckboxAlignment = (this.getAttribute(TABLE_CONSTANTS.attributes.SELECT_CHECKBOX_ALIGNMENT) || CellAlign.Center);
                break;
            case TABLE_CONSTANTS.attributes.TOOLTIP_SELECT:
                this.tooltipSelect = newValue;
                break;
            case TABLE_CONSTANTS.attributes.TOOLTIP_SELECT_ALL:
                this.tooltipSelectAll = newValue;
                break;
        }
    };
    /**
     * Hides a column from the table.
     * @param columnIndex The index of the column to hide.
     */
    TableComponent.prototype.hideColumn = function (columnIndex) {
        this._foundation.hideColumn(columnIndex);
    };
    /**
     * Shows a hidden column in th table.
     * @param columnIndex The index of the column to show.
     */
    TableComponent.prototype.showColumn = function (columnIndex) {
        this._foundation.showColumn(columnIndex);
    };
    /** Returns the selected row instances. */
    TableComponent.prototype.getSelectedRows = function () {
        return this._foundation.getSelectedRows();
    };
    /**
     * Selects a row in the table. Only applicable if `select` is true.
     * @param data The row data behind the row that should be selected.
     */
    TableComponent.prototype.selectRow = function (data) {
        this._foundation.selectRows([data], false);
    };
    /**
     * Selects one or more rows in the table. Only applicable if `select` is true.
     * @param data The row data begind the rows that should be selected.
     * @param [preserveExisting=false] Should existing row selections be preserved or cleared.
     */
    TableComponent.prototype.selectRows = function (data, preserveExisting) {
        if (preserveExisting === void 0) { preserveExisting = false; }
        this._foundation.selectRows(data, !preserveExisting);
    };
    /**
     * Deselects a single row in the table.
     * @param data The data behind the row that should be deselected.
     */
    TableComponent.prototype.deselectRow = function (data) {
        this._foundation.deselectRows([data]);
    };
    /**
     * Deselects one or more rows in the table.
     * @param data The data behind the rows that should be deselected.
     */
    TableComponent.prototype.deselectRows = function (data) {
        this._foundation.deselectRows(data);
    };
    /**
     * Clears all selected table rows.
     */
    TableComponent.prototype.clearSelections = function () {
        this._foundation.clearSelections();
    };
    /**
     * Forces the table to re-render based on its current configuration.
     */
    TableComponent.prototype.render = function () {
        this._foundation.render();
    };
    /**
     * Expands a collapsed row.
     * @param rowIndex The index of the row to expand.
     */
    TableComponent.prototype.expandRow = function (rowIndex, template) {
        return this._foundation.expandRow(rowIndex, template);
    };
    /**
     * Collapses an expanded row.
     * @param rowIndex The index of the row to expand.
     */
    TableComponent.prototype.collapseRow = function (rowIndex) {
        return this._foundation.collapseRow(rowIndex);
    };
    /**
     * Checks if a row is expanded or not.
     * @param rowIndex The row index to check.
     */
    TableComponent.prototype.isRowExpanded = function (rowIndex) {
        return this._foundation.isRowExpanded(rowIndex);
    };
    /**
     * Selects a rows by an index or array of indexes.
     * @param indexes The row index/indexes to select.
     */
    TableComponent.prototype.selectRowsByIndex = function (indexes, preserveExisting) {
        if (typeof indexes === 'number') {
            this._foundation.selectRowsByIndex([indexes], preserveExisting);
        }
        else if (Array.isArray(indexes)) {
            this._foundation.selectRowsByIndex(indexes, preserveExisting);
        }
    };
    /**
     * Deselects a rows by an index or array of indexes.
     * @param indexes The row index/indexes to deselect
     */
    TableComponent.prototype.deselectRowsByIndex = function (indexes) {
        if (typeof indexes === 'number') {
            this._foundation.deselectRowsByIndex([indexes]);
        }
        else if (Array.isArray(indexes)) {
            this._foundation.deselectRowsByIndex(indexes);
        }
    };
    /**
     * Checks if a row is selected or not.
     * @param rowData The row object data to check.
     */
    TableComponent.prototype.isRowSelected = function (rowData) {
        return this._foundation.isRowSelected(rowData);
    };
    Object.defineProperty(TableComponent.prototype, "data", {
        get: function () {
            return this._foundation.data;
        },
        /** The data to be display in the table body. */
        set: function (value) {
            this._foundation.data = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableComponent.prototype, "columnConfigurations", {
        get: function () {
            return this._foundation.columnConfigurations;
        },
        /** The column configuration options. */
        set: function (value) {
            this._foundation.columnConfigurations = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableComponent.prototype, "select", {
        get: function () {
            return this._foundation.select;
        },
        /** Controls the visibility of the select column. */
        set: function (value) {
            this._foundation.select = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableComponent.prototype, "multiselect", {
        get: function () {
            return this._foundation.multiselect;
        },
        /** Controls the visibility of the select all checkbox (only applied when `select` is `true`). */
        set: function (value) {
            this._foundation.multiselect = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableComponent.prototype, "selectKey", {
        get: function () {
            return this._foundation.selectKey;
        },
        /** The row key for matching data to selections. */
        set: function (value) {
            this._foundation.selectKey = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableComponent.prototype, "tooltipSelect", {
        get: function () {
            return this._foundation.tooltipSelect;
        },
        set: function (value) {
            this._foundation.tooltipSelect = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableComponent.prototype, "tooltipSelectAll", {
        get: function () {
            return this._foundation.tooltipSelectAll;
        },
        set: function (value) {
            this._foundation.tooltipSelectAll = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableComponent.prototype, "dense", {
        get: function () {
            return this._foundation.dense;
        },
        /** Controls whether the table is dense or not. False by default. */
        set: function (value) {
            this._foundation.dense = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableComponent.prototype, "roomy", {
        get: function () {
            return this._foundation.roomy;
        },
        /** Controls whether the table is roomy or not. False by default. */
        set: function (value) {
            this._foundation.roomy = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableComponent.prototype, "filter", {
        get: function () {
            return this._foundation.filter;
        },
        /** Controls whether the table filters are visible or not. */
        set: function (value) {
            this._foundation.filter = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableComponent.prototype, "fixedHeaders", {
        get: function () {
            return this._foundation.fixedHeaders;
        },
        /** Controls whether the table applies fixed headers when in scroll containers. */
        set: function (value) {
            this._foundation.fixedHeaders = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableComponent.prototype, "layoutType", {
        get: function () {
            return this._foundation.layoutType;
        },
        /** Controls the table layout algorithm. */
        set: function (value) {
            this._foundation.layoutType = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableComponent.prototype, "wrapContent", {
        get: function () {
            return this._foundation.wrapContent;
        },
        /** Controls whether the content in each cell wraps or not (true by default). */
        set: function (value) {
            this._foundation.wrapContent = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableComponent.prototype, "resizable", {
        get: function () {
            return this._foundation.resizable;
        },
        /** Controls whether the columns are resizable or not. */
        set: function (value) {
            this._foundation.resizable = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableComponent.prototype, "minResizeWidth", {
        /** Gets/sets the minimum width that a column can be resized to by the user dragging the resize handle. */
        get: function () {
            return this._foundation.minResizeWidth;
        },
        set: function (value) {
            this._foundation.minResizeWidth = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableComponent.prototype, "allowRowClick", {
        /** Gets/sets whether the rows respond to (and emit) row click events. */
        get: function () {
            return this._foundation.allowRowClick;
        },
        set: function (value) {
            this._foundation.allowRowClick = value;
        },
        enumerable: true,
        configurable: true
    });
    __decorate([
        FoundationProperty()
    ], TableComponent.prototype, "multiColumnSort", void 0);
    __decorate([
        FoundationProperty({ get: false })
    ], TableComponent.prototype, "rowCreated", void 0);
    __decorate([
        FoundationProperty({ get: false })
    ], TableComponent.prototype, "cellCreated", void 0);
    __decorate([
        FoundationProperty()
    ], TableComponent.prototype, "selectAllTemplate", void 0);
    __decorate([
        FoundationProperty()
    ], TableComponent.prototype, "selectCheckboxAlignment", void 0);
    TableComponent = __decorate([
        CustomElement({
            name: TABLE_CONSTANTS.elementName,
            dependencies: [
                ExpansionPanelComponent,
                IconComponent
            ]
        })
    ], TableComponent);
    return TableComponent;
}(HTMLElement));
export { TableComponent };
